/* eslint-disable operator-linebreak */
import React, { useCallback, useEffect, useState, useMemo, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import cc from 'classnames';
import * as yup from 'yup';
import { FormSpy } from 'react-final-form';
import { CircularProgress } from '@material-ui/core';
import MuiButton from '@material-ui/core/Button';
import Form from 'ui/Form/Form';
import Button from 'ui/Button/Button';
import PINInput from 'ui/Input/PINInput/PINInput';
import TransactionCard, { ITransactionField } from 'ui/structure/TransactionCard';
import NavigationTitle, { LayoutType } from 'ui/NavigationTitle/NavigationTitle';
import { AxiosStatus } from 'api/axiosConfig';
import noop from 'utils/noop';
import TimerCountDown from 'utils/timer';
import { getMinutes, getSeconds } from 'utils/formatters';
import { validateOtp, requiredOnly } from 'utils/validation';
import { mapYupObjectError, objectMap } from 'utils/helpers';
import Input from 'ui/Input';
import routePaths from 'routes/routePaths';
import { NETWORK_REQUIRED } from 'utils/constants';
import { IOTPError } from 'components/authModules/LogIn/login.model';
import { IStatusModalProps } from '../IModalProps';
import ModalWindow from '../ModalWindow/ModalWindow';
import SpinnerModal from '../SpinnerModal/SpinnerModal';
import StatusModal from '../StatusModal/StatusModal';

import styles from './OTPConfirmationModal.module.css';

const buildSchema = (askBeneficiaryName: boolean) =>
  yup.object().shape({
    pin: validateOtp(),
    ...(askBeneficiaryName ? { beneficiaryName: requiredOnly('Beneficiary Name') } : {}),
  });

interface IFormValues {
  pin: string;
  beneficiaryName: string;
}

interface IProps extends Pick<IStatusModalProps, 'onSuccess' | 'onRetry' | 'getReceipt'> {
  open: boolean;
  data?: ITransactionField[];
  askSaveBeneficiary?: boolean;
  disableSaveBeneficiary?: boolean;
  defaultSaveBeneficiary?: boolean;
  askBeneficiaryName?: boolean;
  submitStatus: AxiosStatus;
  successText: string;
  failureText: string;
  onResend: () => Promise<any>;
  OTPPinError: IOTPError | null;
  onSubmit: (pin: string, saveBeneficiary: boolean, beneficiaryName: string) => void;
  onClose: () => void;
}

const OTPConfirmationModal = ({
  open,
  data,
  onSubmit,
  onClose,
  onSuccess,
  onResend,
  OTPPinError,
  getReceipt,
  askSaveBeneficiary,
  disableSaveBeneficiary,
  defaultSaveBeneficiary,
  askBeneficiaryName,
  submitStatus,
  successText,
  failureText,
}: IProps) => {
  const timer = useMemo(() => new TimerCountDown(), []);
  const [timeLeft, setTimeLeft] = useState<number | undefined>();
  const [resending, setResending] = useState(false);
  const [showPinWrong, setShowPinWrong] = useState(false);
  const [saveBeneficiary, setSaveBeneficiary] = useState(false);
  const requestRef = useRef<number | undefined>();

  const animate: FrameRequestCallback = () => {
    setTimeLeft(timer.time);
    requestRef.current = requestAnimationFrame(animate);
  };

  const stopTimer = useCallback(() => {
    timer.stop();
    if (requestRef.current != null) {
      cancelAnimationFrame(requestRef.current);
    }
  }, [timer]);

  const onResendOtp = useCallback(
    (pin) => () => {
      setResending(true);
      onResend()
        .then(() => {
          if (open) {
            timer.start(2 * 60);
            pin.reset();
          } else {
            stopTimer();
          }
        })
        .catch((error) => {
          console.error(error || NETWORK_REQUIRED);
        })
        .finally(() => setResending(false));
    },
    [onResend, open, stopTimer, timer],
  );

  const timeIsOver = useMemo(() => timeLeft != null && timeLeft <= 0, [timeLeft]);

  const handleOnClose = () => {
    stopTimer();
    onClose();
  };

  const handleOnResendOtp = useCallback((pin) => (timeIsOver ? onResendOtp(pin) : noop), [onResendOtp, timeIsOver]);
  const history = useHistory();
  useEffect(() => {
    const { failureCount, description } = OTPPinError || {};
    if (failureCount === 0 || description === 'OTP validation: Otp is nullified') {
      setResending(true);
      onResend()
        .then(() => {
          if (open) {
            timer.start(2 * 60);
          } else {
            stopTimer();
            history.push(routePaths.dashboard);
          }
        })
        .catch((error) => {
          console.error(error || NETWORK_REQUIRED);
        })
        .finally(() => setResending(false));
    }
  }, []);

  const isStatusModalOpen =
    submitStatus === AxiosStatus.SUCCESS ||
    submitStatus === AxiosStatus.FAILURE ||
    submitStatus === AxiosStatus.PIN_BLOCKED;
  const showSpinner = submitStatus === AxiosStatus.PENDING || submitStatus === AxiosStatus.PIN_PENDING;
  const shouldAskBenName = useMemo(
    () => !!(askSaveBeneficiary && askBeneficiaryName && saveBeneficiary),
    [askBeneficiaryName, askSaveBeneficiary, saveBeneficiary],
  );
  const schema = useMemo(() => buildSchema(shouldAskBenName), [shouldAskBenName]);

  useEffect(() => {
    if (submitStatus === AxiosStatus.FIRST_WRONG_PIN || submitStatus === AxiosStatus.SECOND_WRONG_PIN) {
      setShowPinWrong(true);
    }
  }, [submitStatus]);

  const onChangeForm = useCallback(() => {
    if (showPinWrong) setShowPinWrong(false);
  }, [showPinWrong, submitStatus]);

  useEffect(() => {
    if (open) {
      timer.start(2 * 60);
      requestRef.current = requestAnimationFrame(animate);
    } else {
      stopTimer();
    }
    return stopTimer;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleConfirm = useCallback(
    ({ pin, beneficiaryName }: IFormValues) => {
      const shouldSaveBen = askSaveBeneficiary === true && saveBeneficiary;
      const benName = shouldAskBenName ? beneficiaryName : '';
      onSubmit(pin, shouldSaveBen, benName);
    },
    [askSaveBeneficiary, onSubmit, saveBeneficiary, shouldAskBenName],
  );

  const validate = useCallback(
    (values: IFormValues) => {
      try {
        schema.validateSync(values, { abortEarly: false });
      } catch (e) {
        const valErrs = mapYupObjectError<IFormValues>(e);
        return objectMap(valErrs, (v) => v[0]);
      }
      return undefined;
    },
    [schema],
  );

  const onToggleSaveBeneficiary = useCallback((saveBen) => setSaveBeneficiary(saveBen), []);

  if (!open) {
    return null;
  }

  const getHelperText = (error: string, touched) => {
    if (submitStatus === AxiosStatus.FIRST_WRONG_PIN && showPinWrong) {
      return 'OTP is not valid.';
    }
    if (submitStatus === AxiosStatus.SECOND_WRONG_PIN && showPinWrong) {
      return 'OTP is not valid.';
    }
    if (error && touched) {
      return error;
    }
    return '';
  };

  const pin = (
    <Form
      onSubmit={handleConfirm}
      validate={validate}
      render={({ handleSubmit, errors, touched, hasValidationErrors }) => (
        <form className={styles.pinForm} onSubmit={handleSubmit}>
          {shouldAskBenName && (
            <div>
              <Input name="beneficiaryName" label="Enter Beneficiary Name" type="text" />
            </div>
          )}

          <div className={cc({ [styles.withDivider]: !!data })}>
            <div className={styles.verificationTitle}>OTP Verification</div>
            <div className={styles.verificationSubTitle}>
              An OTP has been sent to your mobile number and e-mail. Please enter the 5 digit OTP in the field below.
            </div>
            <PINInput
              name="pin"
              label="Enter OTP"
              error={touched?.pin && hasValidationErrors}
              helperText={getHelperText(errors?.pin, touched?.pin)}
            />
          </div>
          <div className={styles.resendContainer}>
            {getMinutes(timeLeft)}:{getSeconds(timeLeft)}
            <MuiButton
              color="primary"
              onClick={handleOnResendOtp(pin)}
              className={styles.resendButton}
              disabled={!timeIsOver}
            >
              Resend OTP{resending && <CircularProgress size={16} color="inherit" className={styles.spinner} />}
            </MuiButton>
          </div>
          <Button type="submit" className={styles.btnSubmit} fullWidth>
            Confirm
          </Button>
          <FormSpy onChange={onChangeForm} />
        </form>
      )}
    />
  );

  return (
    <>
      <ModalWindow open={open && !isStatusModalOpen} onClose={handleOnClose} fullScreen>
        <>
          <NavigationTitle to="#/" onClick={onClose} layoutType={LayoutType.dashboard} className={styles.navTitle}>
            Confirmation
          </NavigationTitle>
          {data && (
            <TransactionCard
              fields={data}
              askSaveBeneficiary={askSaveBeneficiary}
              onToggleSaveBeneficiary={onToggleSaveBeneficiary}
              disableSaveBeneficiary={disableSaveBeneficiary}
              defaultSaveBeneficiary={defaultSaveBeneficiary}
              className={styles.card}
            />
          )}
          {pin}
        </>
      </ModalWindow>
      <SpinnerModal open={showSpinner} onClose={onClose} />
      <StatusModal
        open={isStatusModalOpen}
        status={submitStatus === AxiosStatus.SUCCESS ? 'success' : 'failure'}
        onClose={onClose}
        onSuccess={onSuccess}
        getReceipt={getReceipt}
        onRetryText="Exit"
      >
        {submitStatus === AxiosStatus.SUCCESS ? successText : failureText}
      </StatusModal>
    </>
  );
};

export default OTPConfirmationModal;
